/**
 * Active sessions (PRD §12, §16).
 *
 * Lets a user see where their account is signed in and end any of those
 * sessions — the standard remedy when a device is lost or shared.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SessionSummary } from '@mindpeer/auth-shared';
import { Alert, Button, Card, CardHeader, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, api } from '@/lib/api';
import { cn, describeUserAgent, formatDateTime, formatRelative } from '@/lib/format';

export function SessionsPage() {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { sessions: items } = await api.sessions();
      setSessions(items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your sessions.');
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = useCallback(
    (session: SessionSummary) => {
      setRevoking(session.id);
      setError(null);
      void (async () => {
        try {
          await api.revokeSession(session.id);
          // Revoking the current session clears this browser's cookie server
          // side, so the UI must stop claiming to be signed in.
          if (session.current) {
            setUser(null);
            navigate('/sign-in', { replace: true });
            return;
          }
          await load();
        } catch (caught) {
          setError(caught instanceof ApiError ? caught.message : 'Could not end that session.');
        } finally {
          setRevoking(null);
        }
      })();
    },
    [load, navigate, setUser],
  );

  if (sessions === null) {
    return (
      <div className="flex justify-center py-12" role="status">
        <Spinner className="size-6 text-brand-600" />
        <span className="sr-only">Loading your sessions</span>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Active sessions"
        description="Every browser and device currently signed in to your MindPeer account."
      />

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {sessions.length === 0 ? (
        <Alert tone="info">No active sessions.</Alert>
      ) : (
        <ul className="space-y-3">
          {sessions.map((session) => (
            <li
              key={session.id}
              className={cn(
                'flex flex-wrap items-start justify-between gap-3 rounded-lg p-4 ring-1',
                session.current ? 'bg-brand-50 ring-brand-200' : 'ring-slate-200',
              )}
            >
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium text-slate-900">
                  {describeUserAgent(session.userAgent)}
                  {session.current && (
                    <span className="ml-2 rounded-full bg-brand-600 px-2 py-0.5 text-xs font-medium text-white">
                      This device
                    </span>
                  )}
                </p>
                <p className="text-sm text-slate-600">
                  {session.ipAddress ?? 'Unknown location'} · last active{' '}
                  {formatRelative(session.lastSeenAt)}
                </p>
                <p className="text-xs text-slate-500">
                  Signed in {formatDateTime(session.createdAt)} · expires{' '}
                  {formatDateTime(session.expiresAt)}
                </p>
              </div>

              <Button
                variant={session.current ? 'secondary' : 'danger'}
                loading={revoking === session.id}
                disabled={revoking !== null}
                onClick={() => revoke(session)}
              >
                {session.current ? 'Sign out here' : 'End session'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
